import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, getToken, setToken } from '../api/client';

export interface User {
  id: string;
  email?: string | null;
  displayName: string;
  avatarUrl?: string;
  streak?: number;
  plan?: string;
  isPremium?: boolean;
  premiumUntil?: string | null;
  eloRating?: number;
  arenaMatchesPlayed?: number;
  isAdmin?: boolean;
  isGuest?: boolean;
  notifyEmailEnabled?: boolean;
  notifyPushEnabled?: boolean;
  notifyTime?: string;
  notifyPromptSeen?: boolean;
}

export interface NotificationPrefsUpdate {
  notifyEmailEnabled?: boolean;
  notifyPushEnabled?: boolean;
  notifyTime?: string;
  notifyPromptSeen?: boolean;
}

interface AuthState {
  user: User | null;
  /** @deprecated use user?.isGuest */
  guest: boolean;
  loading: boolean;
  loginWithToken: (token: string) => Promise<void>;
  requestMagicLink: (email: string, displayName: string) => Promise<void>;
  loginGuest: () => Promise<User>;
  logout: () => void;
  updateDisplayName: (displayName: string) => Promise<void>;
  updateNotificationPrefs: (prefs: NotificationPrefsUpdate) => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api<User & { token?: string }>('/auth/me');
      if (me.token) setToken(me.token);
      const { token: _token, ...user } = me;
      setUser(user);
    } catch {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loginWithToken = useCallback(async (token: string) => {
    setToken(token);
    await refresh();
  }, [refresh]);

  const requestMagicLink = async (email: string, displayName: string) => {
    await api('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email, displayName }),
    });
  };

  const loginGuest = useCallback(async () => {
    const res = await api<{ token: string; guestToken: string; user: User }>('/auth/guest', {
      method: 'POST',
    });
    setToken(res.token || res.guestToken);
    setUser(res.user);
    return res.user;
  }, []);

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  const updateDisplayName = useCallback(async (displayName: string) => {
    await api<{ displayName: string }>('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify({ displayName }),
    });
    await refresh();
  }, [refresh]);

  const updateNotificationPrefs = useCallback(async (prefs: NotificationPrefsUpdate) => {
    await api('/auth/notification-prefs', {
      method: 'PATCH',
      body: JSON.stringify(prefs),
    });
    await refresh();
  }, [refresh]);

  const guest = !!user?.isGuest;

  const value = useMemo(() => ({
    user,
    guest,
    loading,
    loginWithToken,
    requestMagicLink,
    loginGuest,
    logout,
    updateDisplayName,
    updateNotificationPrefs,
    refreshSession: refresh,
  }), [user, guest, loading, loginWithToken, loginGuest, updateDisplayName, updateNotificationPrefs, refresh]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside provider');
  return ctx;
}
